import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  type FeatureVisibilityConfig,
  type FeatureVisibilityDomainGroup,
  type FeatureVisibilityDomainId,
  FEATURE_VISIBILITY_DEFAULTS,
  FEATURE_VISIBILITY_DOMAIN_GROUPS,
} from '@/services/featureVisibilityService';

function routePathToLocaleSuffix(path: string): string {
  return path.slice(1).replace(/[/-]/g, '_');
}

function countActiveInGroup(
  value: FeatureVisibilityConfig,
  group: FeatureVisibilityDomainGroup
): { active: number; total: number } {
  let active = 0;
  let total = 0;

  for (const id of group.modules) {
    total += 1;
    if (value.modules?.[id]?.visible !== false) active += 1;
  }
  for (const path of group.routes) {
    total += 1;
    if (value.routes?.[path]?.enabled !== false) active += 1;
  }
  for (const key of group.actions) {
    total += 1;
    const defaultEnabled = FEATURE_VISIBILITY_DEFAULTS.actions?.[key]?.enabled ?? false;
    const enabled = value.actions?.[key]?.enabled ?? defaultEnabled;
    if (enabled) active += 1;
  }

  return { active, total };
}

interface FeatureVisibilityEditorProps {
  value: FeatureVisibilityConfig;
  onChange: (next: FeatureVisibilityConfig) => void;
  disabled?: boolean;
}

export function FeatureVisibilityEditor({ value, onChange, disabled }: FeatureVisibilityEditorProps) {
  const { t } = useTranslation();
  const [openDomain, setOpenDomain] = useState<FeatureVisibilityDomainId | null>(null);

  const setModuleVisible = (id: string, visible: boolean) => {
    onChange({
      ...value,
      modules: { ...value.modules, [id]: { visible } },
    });
  };

  const setRouteEnabled = (path: string, enabled: boolean) => {
    onChange({
      ...value,
      routes: { ...value.routes, [path]: { enabled } },
    });
  };

  const setActionEnabled = (key: string, enabled: boolean) => {
    onChange({
      ...value,
      actions: { ...value.actions, [key]: { enabled } },
    });
  };

  const checkboxClass =
    'h-4 w-4 text-stocker-primary border-gray-300 rounded focus:ring-stocker-primary disabled:opacity-50';

  const toggleDomain = (id: FeatureVisibilityDomainId) => {
    setOpenDomain((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-2">
      {FEATURE_VISIBILITY_DOMAIN_GROUPS.map((group) => {
        const open = openDomain === group.id;
        const { active, total } = countActiveInGroup(value, group);
        const panelId = `fv-domain-${group.id}`;

        return (
          <div key={group.id} className="overflow-hidden rounded-md border border-gray-200">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 bg-gray-50 px-3 py-2.5 text-left hover:bg-gray-100"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggleDomain(group.id)}
            >
              <span className="text-sm font-semibold text-gray-800">
                {t(`settings.featureVisibility.domains.${group.id}`)}
              </span>
              <span className="flex items-center gap-2">
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                  {t('settings.featureVisibility.domainActiveCount', { active, total })}
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </span>
            </button>

            {open ? (
              <div id={panelId} className="space-y-5 border-t border-gray-100 bg-white px-3 py-4">
                {group.modules.length > 0 ? (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('settings.featureVisibility.sections.modules')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {group.modules.map((id) => (
                        <label
                          key={id}
                          className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            className={checkboxClass}
                            disabled={disabled}
                            checked={value.modules?.[id]?.visible !== false}
                            onChange={(e) => setModuleVisible(id, e.target.checked)}
                          />
                          <span>{t(`settings.featureVisibility.modules.${id}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}

                {group.routes.length > 0 ? (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('settings.featureVisibility.sections.routes')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {group.routes.map((path) => {
                        const sk = routePathToLocaleSuffix(path);
                        return (
                          <label
                            key={path}
                            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              className={checkboxClass}
                              disabled={disabled}
                              checked={value.routes?.[path]?.enabled !== false}
                              onChange={(e) => setRouteEnabled(path, e.target.checked)}
                            />
                            <span>{t(`settings.featureVisibility.routes.${sk}`)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {group.actions.length > 0 ? (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('settings.featureVisibility.sections.actions')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {group.actions.map((key) => {
                        const defaultEnabled =
                          FEATURE_VISIBILITY_DEFAULTS.actions?.[key]?.enabled ?? false;
                        const enabled = value.actions?.[key]?.enabled ?? defaultEnabled;
                        return (
                          <label
                            key={key}
                            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              className={checkboxClass}
                              disabled={disabled}
                              checked={enabled}
                              onChange={(e) => setActionEnabled(key, e.target.checked)}
                            />
                            <span>
                              {t(
                                `settings.featureVisibility.actions.${key.replace(/\./g, '_')}`
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
